import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../../providers/TenantProvider';

export interface Subscription {
  id: string;
  tenantId: string;
  plan: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEnd?: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
}

export interface UsageData {
  users: {
    included: number;
    used: number;
  };
  storage: {
    includedGb: number;
    usedGb: number;
  };
}

export interface Invoice {
  id: string;
  number: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  pdfUrl?: string;
}

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  last4: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
}

interface Plan {
  id: 'free' | 'pro' | 'enterprise';
  name: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
  usersIncluded: number;
  storageGb: number;
}

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    interval: 'month',
    features: ['5 users', '1GB storage', 'Basic support'],
    usersIncluded: 5,
    storageGb: 1,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    interval: 'month',
    features: ['25 users', '10GB storage', 'Priority support', 'Advanced analytics'],
    usersIncluded: 25,
    storageGb: 10,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 99,
    interval: 'month',
    features: [
      'Unlimited users',
      '100GB storage',
      '24/7 support',
      'Custom integrations',
      'Dedicated account manager',
    ],
    usersIncluded: -1, // unlimited
    storageGb: 100,
  },
];

export function useBilling() {
  const { getAccessTokenSilently } = useAuth0();
  const { currentTenant } = useTenant();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!currentTenant) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/subscription`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch subscription');
      }

      const data = await response.json();
      setSubscription(data.subscription);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, getAccessTokenSilently]);

  const fetchUsage = useCallback(async () => {
    if (!currentTenant) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/usage`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch usage');
      }

      const data = await response.json();
      setUsage(data.usage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  }, [currentTenant, getAccessTokenSilently]);

  const fetchInvoices = useCallback(async () => {
    if (!currentTenant) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/invoices`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  }, [currentTenant, getAccessTokenSilently]);

  const fetchPaymentMethods = useCallback(async () => {
    if (!currentTenant) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/payment-methods`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
      }

      const data = await response.json();
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  }, [currentTenant, getAccessTokenSilently]);

  const changePlan = useCallback(
    async (planId: 'free' | 'pro' | 'enterprise') => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/subscription`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify({ plan: planId }),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to change plan');
        }

        await fetchSubscription();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently, fetchSubscription]
  );

  const cancelSubscription = useCallback(async () => {
    if (!currentTenant) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/subscription/cancel`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to cancel subscription');
      }

      await fetchSubscription();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, getAccessTokenSilently, fetchSubscription]);

  const reactivateSubscription = useCallback(async () => {
    if (!currentTenant) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/subscription/reactivate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to reactivate subscription');
      }

      await fetchSubscription();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, getAccessTokenSilently, fetchSubscription]);

  const createCheckoutSession = useCallback(
    async (planId: 'pro' | 'enterprise') => {
      if (!currentTenant) return null;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/checkout`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify({
              plan: planId,
              successUrl: `${window.location.origin}/billing?success=true`,
              cancelUrl: `${window.location.origin}/billing?canceled=true`,
            }),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to create checkout session');
        }

        const data = await response.json();
        return data.url;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  const createPortalSession = useCallback(async () => {
    if (!currentTenant) return null;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/billing/${currentTenant.id}/portal`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-tenant-id': currentTenant.id,
          },
          body: JSON.stringify({
            returnUrl: `${window.location.origin}/billing`,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to create portal session');
      }

      const data = await response.json();
      return data.url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, getAccessTokenSilently]);

  return {
    subscription,
    usage,
    invoices,
    paymentMethods,
    isLoading,
    error,
    fetchSubscription,
    fetchUsage,
    fetchInvoices,
    fetchPaymentMethods,
    changePlan,
    cancelSubscription,
    reactivateSubscription,
    createCheckoutSession,
    createPortalSession,
    plans: PLANS,
  };
}
